import { loanEditorCustomFormsApi } from 'app/services/api';

import { catchAsyncActionError } from 'utils/action';

import { showMessageAction } from 'store/global/actions';
import { LOAN_EDITOR_CUSTOM_FORMS_ACTION_TYPE } from 'store/system/types/loanEditorCustomForms.types';

import {
  LOAN_EDITOR_CUSTOM_FORMS_ERROR_MESSAGES,
  LOAN_EDITOR_CUSTOM_FORMS_SUCCESS_MESSAGES,
} from './messages';

const setFormsAction = forms => ({
  type: LOAN_EDITOR_CUSTOM_FORMS_ACTION_TYPE.SET_FORMS,
  payload: forms,
});

const upsertFormAction = form => ({
  type: LOAN_EDITOR_CUSTOM_FORMS_ACTION_TYPE.UPSERT_FORM,
  payload: form,
});

const removeFormAction = id => ({
  type: LOAN_EDITOR_CUSTOM_FORMS_ACTION_TYPE.REMOVE_FORM,
  payload: id,
});

const setLoadingAction = isLoading => ({
  type: LOAN_EDITOR_CUSTOM_FORMS_ACTION_TYPE.SET_LOADING,
  payload: isLoading,
});

export const loadLoanEditorCustomFormsAction = catchAsyncActionError(
  (params = {}) =>
    async dispatch => {
      dispatch(setLoadingAction(true));

      try {
        const response = await loanEditorCustomFormsApi.getLoanEditorCustomForms(params);
        const forms = Array.isArray(response.data) ? response.data : [];
        dispatch(setFormsAction(forms));

        return forms;
      } finally {
        dispatch(setLoadingAction(false));
      }
    },
  LOAN_EDITOR_CUSTOM_FORMS_ERROR_MESSAGES.LOAD,
);

export const loadLoanEditorCustomFormAction = catchAsyncActionError(
  ({ id, includeGlobal }) =>
    async dispatch => {
      dispatch(setLoadingAction(true));

      try {
        const response = await loanEditorCustomFormsApi.getLoanEditorCustomForm({
          id,
          includeGlobal,
        });
        dispatch(upsertFormAction(response.data));

        return response.data;
      } finally {
        dispatch(setLoadingAction(false));
      }
    },
  LOAN_EDITOR_CUSTOM_FORMS_ERROR_MESSAGES.LOAD,
);

export const createLoanEditorCustomFormAction = catchAsyncActionError(
  data => async dispatch => {
    dispatch(setLoadingAction(true));

    try {
      const response = await loanEditorCustomFormsApi.createLoanEditorCustomForm(data);
      const createdForm = response.data;

      if (createdForm?.id) {
        dispatch(upsertFormAction(createdForm));
      }

      dispatch(
        showMessageAction({
          message: LOAN_EDITOR_CUSTOM_FORMS_SUCCESS_MESSAGES.CREATE,
          variant: 'success',
        }),
      );

      return createdForm;
    } finally {
      dispatch(setLoadingAction(false));
    }
  },
  LOAN_EDITOR_CUSTOM_FORMS_ERROR_MESSAGES.CREATE,
);

export const updateLoanEditorCustomFormAction = catchAsyncActionError(
  ({ id, patch }) =>
    async dispatch => {
      if (!id) {
        throw new Error('Missing ID for update');
      }

      dispatch(setLoadingAction(true));

      try {
        const response = await loanEditorCustomFormsApi.updateLoanEditorCustomForm({ id, patch });
        dispatch(upsertFormAction(response.data));
        dispatch(
          showMessageAction({
            message: LOAN_EDITOR_CUSTOM_FORMS_SUCCESS_MESSAGES.UPDATE,
            variant: 'success',
          }),
        );

        return response.data;
      } finally {
        dispatch(setLoadingAction(false));
      }
    },
  LOAN_EDITOR_CUSTOM_FORMS_ERROR_MESSAGES.UPDATE,
);

export const updateLoanEditorCustomFormLayoutAction = catchAsyncActionError(
  ({ id, layout }) =>
    async dispatch => {
      if (!id) {
        throw new Error('Missing ID for layout update');
      }

      dispatch(setLoadingAction(true));

      try {
        const response = await loanEditorCustomFormsApi.updateLoanEditorCustomFormLayout({
          id,
          layout,
        });
        dispatch(upsertFormAction(response.data));
        dispatch(
          showMessageAction({
            message: LOAN_EDITOR_CUSTOM_FORMS_SUCCESS_MESSAGES.UPDATE_LAYOUT,
            variant: 'success',
          }),
        );

        return response.data;
      } finally {
        dispatch(setLoadingAction(false));
      }
    },
  LOAN_EDITOR_CUSTOM_FORMS_ERROR_MESSAGES.UPDATE_LAYOUT,
);

export const deleteLoanEditorCustomFormAction = catchAsyncActionError(
  ({ id }) =>
    async dispatch => {
      if (!id) {
        throw new Error('Missing ID for delete');
      }

      dispatch(setLoadingAction(true));

      try {
        await loanEditorCustomFormsApi.deleteLoanEditorCustomForm({ id });
        dispatch(removeFormAction(id));
        dispatch(
          showMessageAction({
            message: LOAN_EDITOR_CUSTOM_FORMS_SUCCESS_MESSAGES.DELETE,
            variant: 'success',
          }),
        );

        return id;
      } finally {
        dispatch(setLoadingAction(false));
      }
    },
  LOAN_EDITOR_CUSTOM_FORMS_ERROR_MESSAGES.DELETE,
);
